import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import CdrSearch from './pages/CdrSearch';
import './index.css';

function cityFromHost(): string {
  const h = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  if (h.startsWith('clbcdr') || h.includes('longbeach')) return 'Long Beach';
  return 'Santa Monica';
}
function emailDomainFromHost(): string {
  const h = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  if (h.startsWith('clbcdr') || h.includes('longbeach')) return 'longbeach.gov';
  return 'santamonica.gov';
}
function Login({ onDone }: { onDone: () => void }) {
  const city = cityFromHost();
  const emailDomain = emailDomainFromHost();
  React.useEffect(() => { if (typeof document !== 'undefined') document.title = `${city} CDR`; }, [city]);
  const [step, setStep] = React.useState<'email' | 'code'>('email');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  async function req(e: React.FormEvent) {
    e.preventDefault(); if (!email) return; setBusy(true); setMsg('');
    try {
      const r = await fetch('/cdr/auth/otp/request', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email: email.trim() }) });
      if (r.ok) { setStep('code'); setMsg('If that email is authorized, a code is on its way. It expires in 10 minutes.'); }
      else setMsg('Could not send a code. Check the email and try again.');
    } catch { setMsg('Network error.'); } finally { setBusy(false); }
  }
  async function verify(e: React.FormEvent) {
    e.preventDefault(); if (!/^\d{6}$/.test(code)) { setMsg('Enter the 6-digit code.'); return; } setBusy(true); setMsg('');
    try {
      const r = await fetch('/cdr/auth/otp/verify', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email: email.trim(), code }) });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) onDone();
      else setMsg(j.error === 'not_authorized' ? 'That account is not on the CDR access list.' : j.error === 'expired' ? 'Code expired — request a new one.' : j.error === 'locked' ? 'Too many attempts — request a new code.' : 'Incorrect code.');
    } catch { setMsg('Network error.'); } finally { setBusy(false); }
  }
  const inp = 'w-full px-3 py-2.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent';
  return (
    <div className="min-h-screen grid place-items-center p-6">
      <form onSubmit={step === 'email' ? req : verify} className="card p-7 w-full max-w-sm space-y-3">
        <div className="text-lg font-semibold">{city} CDR</div>
        <div className="text-sm text-gray-500">{step === 'email' ? 'Sign in with your city email — we’ll send a one-time code.' : 'Enter the 6-digit code we emailed you.'}</div>
        {step === 'email'
          ? <input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`you@${emailDomain}`} className={inp} />
          : <input autoFocus inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" className={inp + ' text-center text-2xl tracking-[0.4em] font-mono'} />}
        <button disabled={busy} className="w-full py-2.5 rounded-lg bg-tnh-accent text-white font-medium disabled:opacity-50">{busy ? 'Please wait…' : step === 'email' ? 'Send code' : 'Verify & sign in'}</button>
        {step === 'code' && <button type="button" onClick={() => { setStep('email'); setCode(''); setMsg(''); }} className="w-full py-1.5 text-xs text-gray-500">Use a different email</button>}
        {msg && <div className="text-xs text-gray-500">{msg}</div>}
        <div className="text-[11px] text-gray-400 pt-1 text-center">City of {city} · authorized users only</div>
      </form>
    </div>
  );
}

function Standalone() {
  const [authed, setAuthed] = React.useState<null | boolean>(null);
  React.useEffect(() => {
    fetch('/cdr/api/whoami', { credentials: 'include' }).then(r => setAuthed(r.ok)).catch(() => setAuthed(false));
  }, []);
  if (authed === null) return <div className="min-h-screen grid place-items-center text-gray-500 animate-pulse">Loading…</div>;
  if (!authed) return <Login onDone={() => setAuthed(true)} />;
  return <div className="px-3 max-w-[1500px] mx-auto min-h-screen md:h-screen overflow-y-auto md:overflow-hidden"><CdrSearch fullHeight /></div>;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode><BrowserRouter><Standalone /></BrowserRouter></React.StrictMode>,
);
